import { useEffect, useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { BgMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/BgMaster/components/BgMasterFormFields'
import { useCreateBgMaster } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useCreateBgMaster'
import {
  type BgMasterFormInput,
  type BgMasterFormValues,
  buildBgMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/schema'
import { resolveBgMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/serverError'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const BG_MASTER_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.bgMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.bgMaster.serverError.aborted',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.bgMaster.serverError.invalidArgument',
}

export const CreateBgMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateBgMaster()
  const { data: tradingPartnersData } = useListTradingPartners()

  const existingTradingPartnerCodes = useMemo(() => {
    const codes = new Set<string>()
    if (tradingPartnersData !== undefined) {
      for (const partner of tradingPartnersData.items) {
        for (const account of partner.accounts) {
          codes.add(account.code)
        }
      }
    }
    return codes
  }, [tradingPartnersData])

  const schema = useMemo(
    () => buildBgMasterFormSchema(false, existingTradingPartnerCodes),
    [existingTradingPartnerCodes],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    watch,
    isDirty,
  } = useCrudModalForm<BgMasterFormInput, BgMasterFormValues>({
    schema,
    defaultValues: {
      bgCode: '',
      bgName: '',
      licenseType: LicenseType.GENERATION,
      areaCode: AreaCode.TOKYO,
      powerType: undefined,
      repBusinessMasterId: '',
      members: [
        {
          businessMasterId: '',
          isOwn: false,
          validFrom: todayCivilDate(),
          validTo: undefined,
        },
      ],
      plants: [],
      wheelingLinkCode: undefined,
      validFrom: todayCivilDate(),
      validTo: undefined,
      contractIdNo1: '',
      note: '',
    },
    onSubmit: async (values) => {
      const isGeneration = values.licenseType === LicenseType.GENERATION
      await mutateAsync({
        bgCode: values.bgCode,
        bgName: values.bgName,
        licenseType: values.licenseType,
        areaCode: values.areaCode,
        powerType: isGeneration ? values.powerType : undefined,
        repBusinessMasterId: values.repBusinessMasterId,
        members: values.members.map((m) => ({
          businessMasterId: m.businessMasterId,
          isOwn: m.isOwn,
          validFrom: m.validFrom,
          validTo: m.validTo,
        })),
        // 配下発電所は発電BGのみ有効 (AC-05/AC-06)。 非発電では
        // BgPlantMembershipSection が自動でクリアするが、 send 時も defensive
        // に空配列へ倒す。
        plants: isGeneration
          ? values.plants.map((p) => ({
              plantMasterId: p.plantMasterId,
              validFrom: p.validFrom,
              validTo: p.validTo,
            }))
          : [],
        validFrom: values.validFrom,
        validTo: values.validTo,
        contractIdNo1:
          isGeneration &&
          'contractIdNo1' in values &&
          values.contractIdNo1 !== undefined &&
          values.contractIdNo1 !== ''
            ? values.contractIdNo1
            : undefined,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        wheelingLinkCode: values.wheelingLinkCode,
      })
    },
    onClose,
    customErrorKeys: BG_MASTER_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.bgMaster.serverError.unknown',
    resolveDetailedError: resolveBgMasterDetailedError,
  })

  // モーダルオープン時に validFrom を実行日に更新（defaultValues の todayCivilDate() は
  // コンポーネントマウント時に固定化されるため、日跨ぎで開き直しても前日のままになる）。
  // members.0.validFrom（代表事業者行）への追随は BgMasterFormFields 側の
  // AC-OMB-021 同期 effect が validFrom の変化を watch して行うため、ここでは
  // BG 本体の validFrom のみ更新すればよい。
  useEffect(() => {
    if (isOpen) {
      setValue('validFrom', todayCivilDate())
    }
  }, [isOpen, setValue])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      // 入力途中で閉じようとした時に離脱確認ダイアログを出す。
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #occto-bg-modal) は width:min(740px,94vw)。
      // ecloud Modal の size バリアントに 740px 相当は無いため近似値 'lg' を使う
      // (= xs/sm/md/lg/xl の中で最も近い)。
      size="lg"
      title={t('page:adminOcctoMaster.bgMaster.modal.createTitle')}
    >
      <BgMasterFormFields
        control={control}
        errors={errors}
        isEdit={false}
        isOpen={isOpen}
        setValue={setValue}
        watch={watch}
      />
    </CrudModalLayout>
  )
}
